/**
 * SPIN TENET — Interactive Flow Preview Hardware Renderer (WebGL2)
 *
 * Dedicated real-time urban wind laboratory preview renderer:
 * - Operates entirely within the Visualization Worker (OffscreenCanvas).
 * - Continuous 60fps animated 3D particle streamlets with local obstacle deflection and wake shadows.
 * - Non-dominant ground plane with subtle orientation compass and domain context.
 * - Instantaneous response: moving/adding/deleting buildings deflects the flow on the next frame.
 *
 * CRITICAL SCIENTIFIC INTEGRITY CONTRACT:
 * - This is an INTERACTIVE PREVIEW, NOT AUTHORITATIVE CFD.
 * - Does not touch or alter the stored OpenFOAM result.
 * - Clearly distinguished from authoritative solver runs.
 */

import type { ConstructionGeometry } from '@contracts/index';

interface ObstacleData {
  id: string;
  cx: number;
  cz: number;
  hw: number; // half width along local X (metres)
  hd: number; // half depth along local Z (metres)
  height: number;
  radius: number; // bounding radius (metres)
  rotationY: number; // rotation around +Y axis in radians
  cosR: number; // cos(rotationY)
  sinR: number; // sin(rotationY)
  type: string;
  isCircle: boolean;
  circleRadius: number;

  // Stage 04 Precomputed Wind-Aligned Scales (cached on geometry or wind change)
  wProj: number;
  lProj: number;
  wakeLength: number;
  maxUpwindDist: number;
  maxCrosswindDist: number;
  influenceRadiusSq: number;
}

/**
 * Preview Model Tuning Parameters (Section S)
 * Grouped reduced-order visualization parameters, clearly distinguished from CFD physics constants.
 */
export interface UrbanPreviewModelParams {
  // Near-field deflection & stagnation
  nearFieldInfluenceMultiplier: number;
  stagnationStrength: number;
  deflectionStrength: number;
  splitWidthScale: number;
  minSplitWidth: number;

  // Flank acceleration
  flankAccelMax: number;
  flankStreamwiseSigma: number;
  aggregateAccelMax: number;

  // Wake structure
  wakeDeficitMax: number;
  wakeLengthMultiplier: number;
  wakeExpansionRate: number;
  wakeDecayPower: number;

  // Vertical envelope & roof diversion
  verticalClearanceScale: number;
  roofLiftStrength: number;
  downwashStrength: number;

  // Vortical & recirculation (coherent Lamb-Oseen structure)
  vortexStrengthMax: number;
  vortexCoreRadius: number;
  strouhalNumber: number;

  // Aggregate limits
  maxSteeringRatio: number;
}

const PREVIEW_MODEL_PARAMS: UrbanPreviewModelParams = {
  nearFieldInfluenceMultiplier: 1.8,
  stagnationStrength: 0.65,
  deflectionStrength: 0.85,
  splitWidthScale: 0.60,
  minSplitWidth: 1.5,

  flankAccelMax: 0.38,
  flankStreamwiseSigma: 0.75,
  aggregateAccelMax: 0.55,

  wakeDeficitMax: 0.70,
  wakeLengthMultiplier: 4.2,
  wakeExpansionRate: 0.18,
  wakeDecayPower: 1.4,

  verticalClearanceScale: 1.2,
  roofLiftStrength: 0.35,
  downwashStrength: 0.15,

  vortexStrengthMax: 0.22,
  vortexCoreRadius: 2.0,
  strouhalNumber: 0.18,

  maxSteeringRatio: 1.2
};

/**
 * Hermite smoothstep interpolation helper for continuous transitions without derivative cliffs.
 */
function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge1 <= edge0) return x >= edge0 ? 1.0 : 0.0;
  const t = Math.max(0.0, Math.min(1.0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3.0 - 2.0 * t);
}

// ---------------------------------------------------------------------------
// Stage 03.4 Visual Streamlet Constants (Continuous Advected Visual Tracers)
// ---------------------------------------------------------------------------

/** Number of spatially integrated visual sample nodes per streamlet (~12.3m total length). */
const VISUAL_PATH_SAMPLES = 16;

/** Fixed world-space step distance (metres) between adjacent backward RK2 samples. */
const VISUAL_PATH_STEP = 0.82;

/** Half-width of the camera-facing ribbon geometry in world metres (0.20m full head width). */
const RIBBON_HALF_WIDTH = 0.10;

/** Maximum visual tracer pool budget for bounded real-time interactive rendering (Flow Density ceiling: 5,000). */
const MAX_VISUAL_TRACERS = 5000;

/** Default active visual tracer count. */
const DEFAULT_VISUAL_TRACERS = 1500;

// ---------------------------------------------------------------------------
// Stage 05.1 Ground Preview Scalar Field Constants
// ---------------------------------------------------------------------------

/** Ground-level reference plane for pedestrian preview scalar field evaluation (metres). */
const GROUND_SAMPLE_HEIGHT = 1.75;

/** Grid resolution for the 2D ground preview scalar field (128x128 = 16,384 samples). */
const GROUND_GRID_RES = 128;

/** Fast integer hash for deterministic pseudo-random stratified seeding */
function deterministicHash(a: number, b: number, c: number, d: number): number {
  let h = ((a * 73856093) ^ (b * 19349663) ^ (c * 83492791) ^ (d * 2654435761)) | 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296.0;
}

// ---------------------------------------------------------------------------
// Airflow Vector Palette Calibration (Authoritative Mapping)
// ---------------------------------------------------------------------------

export interface AirflowPaletteStop {
  sRatio: number;
  color: [number, number, number]; // [r, g, b] in [0, 1]
  hex: string;
}

/**
 * Authoritative Airflow Velocity Palette Definition
 *
 * Semantic progression (Blue -> Yellow -> Orange -> Red):
 *   sRatio <= 0.55 : #1646A0 deep blue (wake deficit / recirculation)
 *   sRatio =  0.80 : #267BC2 blue (wake recovery / near-ambient entry)
 *   sRatio =  1.00 : #E7C531 strong yellow (nominal ambient wind)
 *   sRatio =  1.30 : #F0A51A yellow-orange (moderate acceleration)
 *   sRatio =  1.55 : #E45B1A orange (strong acceleration)
 *   sRatio >= 1.80 : #D51F1F strong red (extreme corner acceleration)
 */
export const AIRFLOW_PALETTE_STOPS: readonly AirflowPaletteStop[] = [
  { sRatio: 0.55, color: [0.086, 0.275, 0.627], hex: '#1646A0' }, // deep blue
  { sRatio: 0.80, color: [0.149, 0.482, 0.761], hex: '#267BC2' }, // blue
  { sRatio: 1.00, color: [0.906, 0.773, 0.192], hex: '#E7C531' }, // strong yellow
  { sRatio: 1.30, color: [0.941, 0.647, 0.102], hex: '#F0A51A' }, // yellow-orange
  { sRatio: 1.55, color: [0.894, 0.357, 0.102], hex: '#E45B1A' }, // orange
  { sRatio: 1.80, color: [0.835, 0.122, 0.122], hex: '#D51F1F' }, // strong red
] as const;

export const AIRFLOW_MIN_SPEED_RATIO = AIRFLOW_PALETTE_STOPS[0].sRatio; // 0.55
export const AIRFLOW_MAX_SPEED_RATIO = AIRFLOW_PALETTE_STOPS[AIRFLOW_PALETTE_STOPS.length - 1].sRatio; // 1.80

/**
 * Authoritative CSS linear-gradient string dynamically built from AIRFLOW_PALETTE_STOPS.
 * Maps stops across the domain [0.55, 1.80] into exact percentage positions:
 *   0.55 -> 0%
 *   0.80 -> 20%
 *   1.00 -> 36%
 *   1.30 -> 60%
 *   1.55 -> 80%
 *   1.80 -> 100%
 */
export const AIRFLOW_LEGEND_GRADIENT: string = (() => {
  const min = AIRFLOW_PALETTE_STOPS[0].sRatio;
  const max = AIRFLOW_PALETTE_STOPS[AIRFLOW_PALETTE_STOPS.length - 1].sRatio;
  const range = max - min;
  return `linear-gradient(to right, ${AIRFLOW_PALETTE_STOPS.map((stop) => {
    const pct = Math.round(((stop.sRatio - min) / range) * 100);
    return `${stop.hex} ${pct}%`;
  }).join(', ')})`;
})();

/**
 * Authoritative scalar evaluation function for airflow vectors.
 * Smoothly interpolates RGB values across AIRFLOW_PALETTE_STOPS.
 */
export function evaluateAirflowColor(sRatio: number): [number, number, number] {
  const stops = AIRFLOW_PALETTE_STOPS;
  if (sRatio <= stops[0].sRatio) return stops[0].color;
  const last = stops.length - 1;
  if (sRatio >= stops[last].sRatio) return stops[last].color;

  let idx = 0;
  for (let i = 0; i < last; i++) {
    if (sRatio < stops[i + 1].sRatio) {
      idx = i;
      break;
    }
  }
  const s0 = stops[idx].sRatio;
  const s1 = stops[idx + 1].sRatio;
  const t = (sRatio - s0) / (s1 - s0);
  const c0 = stops[idx].color;
  const c1 = stops[idx + 1].color;

  return [
    c0[0] + (c1[0] - c0[0]) * t,
    c0[1] + (c1[1] - c0[1]) * t,
    c0[2] + (c1[2] - c0[2]) * t
  ];
}

interface Particle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  speed: number;
  age: number;
  maxAge: number;
  seed: number;
  speedScale: number;
  alphaScale: number;
}

export class FlowPreviewRenderer {
  private gl: WebGL2RenderingContext | null = null;

  // Ground plane rendering
  private groundProgram: WebGLProgram | null = null;
  private groundVao: WebGLVertexArrayObject | null = null;
  private groundVbo: WebGLBuffer | null = null;
  private groundVertexCount = 0;
  private uGroundViewProjLoc: WebGLUniformLocation | null = null;
  private uGroundWindDirLoc: WebGLUniformLocation | null = null;
  private uGroundDomainHalfLoc: WebGLUniformLocation | null = null;
  private uGroundTextureLoc: WebGLUniformLocation | null = null;

  // Stage 05.1: Ground Scalar Texture Pipeline (128x128 RG8 texture)
  private groundTexture: WebGLTexture | null = null;
  private readonly groundScalarTarget = new Float32Array(GROUND_GRID_RES * GROUND_GRID_RES);
  public readonly groundScalarDisplay = new Float32Array(GROUND_GRID_RES * GROUND_GRID_RES);
  private readonly groundTextureBuffer = new Uint8Array(GROUND_GRID_RES * GROUND_GRID_RES * 2);
  private groundDirty = true;
  private groundTimeSinceRefresh = 0.0;
  private groundInitialBuild = true;

  // Streamlet particle flow rendering (Stage 03: camera-facing triangle ribbons)
  private flowProgram: WebGLProgram | null = null;
  private flowVao: WebGLVertexArrayObject | null = null;
  private flowVbo: WebGLBuffer | null = null;
  private uFlowViewProjLoc: WebGLUniformLocation | null = null;

  // Reusable vertex build buffer — avoids per-frame GC allocations
  private flowVertexBuffer: Float32Array | null = null;

  // Static node workspace for RK2 field integration and ribbon geometry (zero per-frame allocations)
  private static readonly pathX = new Float32Array(32);
  private static readonly pathY = new Float32Array(32);
  private static readonly pathZ = new Float32Array(32);
  private static readonly pathSpeed = new Float32Array(32);

  private static readonly nodeLx = new Float32Array(32);
  private static readonly nodeLy = new Float32Array(32);
  private static readonly nodeLz = new Float32Array(32);
  private static readonly nodeRx = new Float32Array(32);
  private static readonly nodeRy = new Float32Array(32);
  private static readonly nodeRz = new Float32Array(32);
  private static readonly nodeR = new Float32Array(32);
  private static readonly nodeG = new Float32Array(32);
  private static readonly nodeB = new Float32Array(32);
  private static readonly nodeA = new Float32Array(32);

  private static readonly _velOut = { vx: 0.0, vy: 0.0, vz: 0.0 };
  private static readonly _fieldParams = { speedScale: 1.0, seed: 0.0 };

  // Stage 03.4 & Final UI Control: Persistent Visual Tracer Pool (Flow Density: 300 to 5,000)
  private activeVisualTracerCount = DEFAULT_VISUAL_TRACERS;
  private tracerCount = 0; // Total populated slots in preallocated buffer
  private readonly tracerX = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerY = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerZ = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerAge = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerMaxAge = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerSpeedScale = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerSeed = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerBaseAlpha = new Float32Array(MAX_VISUAL_TRACERS);
  private readonly tracerTier = new Uint8Array(MAX_VISUAL_TRACERS);
  // Source kind: 0 = domain/inflow, 1 = obstacle upwind envelope, 2 = gap corridor
  private readonly tracerSourceType = new Uint8Array(MAX_VISUAL_TRACERS);
  private readonly tracerSourceIndex = new Int16Array(MAX_VISUAL_TRACERS);

  // Simulation state
  private readonly PARTICLE_COUNT = 1800;
  private particles: Particle[] = [];
  private obstacles: ObstacleData[] = [];

  // Wind setup
  private windSpeed = 6.0; // m/s
  private windHeadingRad = (25.0 * Math.PI) / 180.0; // flow heading angle
  private domainHalfX = 250.0;
  private domainHalfZ = 250.0;
  private domainHeight = 100.0;

  // Time tracking
  private simTime = 0.0;

  public init(gl: WebGL2RenderingContext): boolean {
    this.gl = gl;

    if (!this.initGroundPipeline(gl)) return false;
    if (!this.initFlowPipeline(gl)) return false;

    this.initParticles();
    this.buildGroundMesh();
    this.refreshObstacleWindScales();
    this.initVisualTracers();
    this.initGroundTexture();
    this.updateGroundScalarGrid(0.0, true);
    return true;
  }

  // ---------------------------------------------------------------------------
  // Pipeline Initialization
  // ---------------------------------------------------------------------------

  private initGroundPipeline(gl: WebGL2RenderingContext): boolean {
    const vsSource = `#version 300 es
      layout(location = 0) in vec3 a_position;
      layout(location = 1) in vec4 a_color;

      uniform mat4 u_viewProj;
      out vec4 v_color;
      out vec3 v_worldPos;

      void main() {
        v_color = a_color;
        v_worldPos = a_position;
        gl_Position = u_viewProj * vec4(a_position, 1.0);
      }
    `;

    const fsSource = `#version 300 es
      precision mediump float;

      in vec4 v_color;
      in vec3 v_worldPos;
      uniform vec2 u_windDir; // (dirX, dirZ)
      uniform vec2 u_domainHalf; // (halfX, halfZ)
      uniform sampler2D u_groundTexture; // RG8: R=speedRatio/2, G=solidMask

      out vec4 fragColor;

      // Piecewise scientific color ramp: Deep Blue -> Strong Blue -> Cyan -> Teal -> Green -> Yellow-Gold -> Orange -> Red
      // Exact semantic breakpoints: 0.45, 0.70, 0.90, 1.10, 1.30, 1.55, 1.80
      vec3 evaluateVelocityColor(float s) {
        const vec3 c0 = vec3(0.078, 0.220, 0.580); // #143894 deep blue (<= 0.25)
        const vec3 c1 = vec3(0.086, 0.408, 0.698); // #1668B2 strong blue (0.45)
        const vec3 c2 = vec3(0.082, 0.596, 0.780); // #1598C7 cyan (0.70)
        const vec3 c3 = vec3(0.082, 0.620, 0.549); // #159E8C teal (0.90)
        const vec3 c4 = vec3(0.180, 0.608, 0.310); // #2E9B4F green (1.10)
        const vec3 c5 = vec3(0.847, 0.710, 0.165); // #D8B52A yellow-gold (1.30)
        const vec3 c6 = vec3(0.898, 0.420, 0.122); // #E56B1F orange (1.55)
        const vec3 c7 = vec3(0.827, 0.125, 0.125); // #D32020 red (>= 1.80)

        if (s <= 0.25) return c0;
        if (s < 0.45) return mix(c0, c1, (s - 0.25) / 0.20);
        if (s < 0.70) return mix(c1, c2, (s - 0.45) / 0.25);
        if (s < 0.90) return mix(c2, c3, (s - 0.70) / 0.20);
        if (s < 1.10) return mix(c3, c4, (s - 0.90) / 0.20);
        if (s < 1.30) return mix(c4, c5, (s - 1.10) / 0.20);
        if (s < 1.55) return mix(c5, c6, (s - 1.30) / 0.25);
        if (s < 1.80) return mix(c6, c7, (s - 1.55) / 0.25);
        return c7;
      }

      void main() {
        float dist = length(v_worldPos.xz);
        float maxDomainRadius = max(u_domainHalf.x, u_domainHalf.y) * 1.05;

        // Subtle light architectural ground slab base
        float slabAlpha = v_color.a * smoothstep(maxDomainRadius * 1.15, maxDomainRadius * 0.50, dist);

        // Map world (x, z) to domain texture coordinates [0, 1]
        vec2 uv = (v_worldPos.xz + u_domainHalf) / (2.0 * u_domainHalf);

        vec3 groundCol = v_color.rgb;
        float totalAlpha = slabAlpha;

        if (uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0) {
          vec2 texSample = texture(u_groundTexture, uv).rg;
          float sRatio = texSample.r * 2.0;
          float solidMask = texSample.g; // 0.0 in air, 1.0 in solid

          // Smooth transition across solid obstacle footprint boundary
          float solidFactor = smoothstep(0.35, 0.65, solidMask);

          // Micro-calibration: ground heatmap visual strength calibrated to 50% of rendered strength
          const float GROUND_HEATMAP_STRENGTH = 0.50;

          // Alpha hierarchy bounded to scientific interest targets (scaled to 50% visual strength):
          // Ambient (sRatio ~ 1.0): 0.15
          // Low wake (sRatio < 1.0): 0.16 - 0.21
          // Moderate accel (sRatio 1.1 - 1.5): 0.18 - 0.23
          // Strong accel (sRatio > 1.5): 0.24 - 0.29, max clamped <= 0.30
          float wakeDeficit = clamp(1.0 - sRatio, 0.0, 1.0);
          float accelExcess = clamp(sRatio - 1.0, 0.0, 1.0);
          float heatmapAlpha = clamp(0.30 + wakeDeficit * 0.14 + accelExcess * 0.32, 0.28, 0.60) * GROUND_HEATMAP_STRENGTH;

          // Radial falloff: active domain (up to 82% max radius) is 100% visible,
          // then gracefully fades out to the computational boundary
          float domainFalloff = smoothstep(maxDomainRadius, maxDomainRadius * 0.82, dist);

          // Active scalar weighting: 0 inside buildings, 1 in open air within domain
          float activeScalarWeight = (1.0 - solidFactor) * domainFalloff;

          vec3 heatCol = evaluateVelocityColor(sRatio);

          // Blend rich scalar field onto ground slab
          groundCol = mix(v_color.rgb, heatCol, activeScalarWeight);
          totalAlpha = mix(slabAlpha, heatmapAlpha, activeScalarWeight);
        }

        // Faint concentric technical distance rings at 50m intervals (subordinate reference)
        float ring = abs(fract(dist / 50.0) - 0.5);
        float ringAccent = smoothstep(0.47, 0.495, ring) * 0.022;

        // Subtle directional streamline wash aligned with wind (subordinate texture)
        float streamGrid = sin(dot(v_worldPos.xz, vec2(-u_windDir.y, u_windDir.x)) * 0.18);
        float flowAccent = smoothstep(0.82, 1.0, streamGrid) * 0.010;

        vec3 col = groundCol - vec3(ringAccent) - vec3(flowAccent * 0.4, flowAccent * 0.2, 0.0);
        fragColor = vec4(col, totalAlpha);
      }
    `;

    const vs = this.compileShader(gl.VERTEX_SHADER, vsSource);
    const fs = this.compileShader(gl.FRAGMENT_SHADER, fsSource);
    if (!vs || !fs) return false;

    const prog = gl.createProgram();
    if (!prog) return false;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);

    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('[FlowPreviewRenderer] Ground shader link error:', gl.getProgramInfoLog(prog));
      return false;
    }

    this.groundProgram = prog;
    this.uGroundViewProjLoc = gl.getUniformLocation(prog, 'u_viewProj');
    this.uGroundWindDirLoc = gl.getUniformLocation(prog, 'u_windDir');
    this.uGroundDomainHalfLoc = gl.getUniformLocation(prog, 'u_domainHalf');
    this.uGroundTextureLoc = gl.getUniformLocation(prog, 'u_groundTexture');

    this.groundVao = gl.createVertexArray();
    this.groundVbo = gl.createBuffer();

    gl.bindVertexArray(this.groundVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.groundVbo);

    // Stride: 7 floats (vec3 pos, vec4 col)
    const STRIDE = 7 * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, STRIDE, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, STRIDE, 3 * 4);

    gl.bindVertexArray(null);
    return true;
  }

  private initGroundTexture(): void {
    if (!this.gl) return;
    const gl = this.gl;

    if (!this.groundTexture) {
      this.groundTexture = gl.createTexture();
    }

    gl.bindTexture(gl.TEXTURE_2D, this.groundTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RG8,
      GROUND_GRID_RES,
      GROUND_GRID_RES,
      0,
      gl.RG,
      gl.UNSIGNED_BYTE,
      this.groundTextureBuffer
    );
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  private initFlowPipeline(gl: WebGL2RenderingContext): boolean {
    const vsSource = `#version 300 es
      layout(location = 0) in vec3 a_position;
      layout(location = 1) in vec4 a_color;
      layout(location = 2) in float a_lateral;

      uniform mat4 u_viewProj;
      out vec4 v_color;
      out float v_lateral;

      void main() {
        v_color = a_color;
        v_lateral = a_lateral;
        gl_Position = u_viewProj * vec4(a_position, 1.0);
      }
    `;

    // Stage 03.1 fragment shader: feathered lateral falloff for soft, airy streamlet edges
    const fsSource = `#version 300 es
      precision mediump float;

      in vec4 v_color;
      in float v_lateral;
      out vec4 fragColor;

      void main() {
        // Smooth parabolic cross-sectional edge falloff: 1.0 at centerline, 0.0 at lateral boundaries.
        // Feathers outer ribbon boundaries to zero opacity, producing soft organic airflow strands.
        float edgeAtten = clamp(1.0 - v_lateral * v_lateral, 0.0, 1.0);
        float softCore = edgeAtten * (0.45 + 0.55 * edgeAtten);
        float a = clamp(v_color.a * softCore, 0.0, 0.85);
        fragColor = vec4(v_color.rgb * a, a);
      }
    `;

    const vs = this.compileShader(gl.VERTEX_SHADER, vsSource);
    const fs = this.compileShader(gl.FRAGMENT_SHADER, fsSource);
    if (!vs || !fs) return false;

    const prog = gl.createProgram();
    if (!prog) return false;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);

    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('[FlowPreviewRenderer] Flow shader link error:', gl.getProgramInfoLog(prog));
      return false;
    }

    this.flowProgram = prog;
    this.uFlowViewProjLoc = gl.getUniformLocation(prog, 'u_viewProj');

    this.flowVao = gl.createVertexArray();
    this.flowVbo = gl.createBuffer();

    gl.bindVertexArray(this.flowVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.flowVbo);

    // Stride: 8 floats (vec3 pos, vec4 col, float lateral)
    const STRIDE = 8 * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, STRIDE, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, STRIDE, 3 * 4);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, STRIDE, 7 * 4);

    gl.bindVertexArray(null);

    // Pre-allocate dynamic triangle vertex array: (VISUAL_PATH_SAMPLES - 1) * 6 vertices * 8 floats
    const maxTrianglesPerStreamlet = (VISUAL_PATH_SAMPLES - 1) * 2;
    const maxFloats = MAX_VISUAL_TRACERS * maxTrianglesPerStreamlet * 3 * 8;
    this.flowVertexBuffer = new Float32Array(maxFloats);

    return true;
  }

  // ---------------------------------------------------------------------------
  // Geometry & Wind Ingestion
  // ---------------------------------------------------------------------------

  public updateGeometry(geometries: ConstructionGeometry[]): void {
    this.obstacles = geometries.map((g) => {
      const cx = g.center.x;
      const cz = g.center.z;
      const isCircle = g.type === 'circle' || (g as any).shapeType === 'circle';
      const circleRadius = (g as any).radius ?? ((g as any).width ? (g as any).width / 2.0 : 3.0);
      const w = (g as any).width ?? (isCircle ? circleRadius * 2.0 : 15.0);
      const d = (g as any).depth ?? (isCircle ? circleRadius * 2.0 : 15.0);
      const hw = Math.max(1.5, w / 2.0);
      const hd = Math.max(1.5, d / 2.0);
      const height = g.height && g.height > 0 ? g.height : (g.type === 'building_mass' ? g.height : 12.0);
      const radius = isCircle ? circleRadius : Math.hypot(hw, hd);
      const rotationY = (g as any).rotationY ?? 0.0;
      const cosR = Math.cos(rotationY);
      const sinR = Math.sin(rotationY);

      return {
        id: g.id,
        cx,
        cz,
        hw,
        hd,
        height,
        radius,
        rotationY,
        cosR,
        sinR,
        type: g.type,
        isCircle,
        circleRadius,
        wProj: isCircle ? circleRadius : hw,
        lProj: isCircle ? circleRadius : hd,
        wakeLength: 40.0,
        maxUpwindDist: 30.0,
        maxCrosswindDist: 30.0,
        influenceRadiusSq: 10000.0
      };
    });

    this.refreshObstacleWindScales();

    if (this.tracerCount === 0) {
      this.initVisualTracers();
    } else {
      // Stage 04: Preserve moving visual population; safely resolve only newly engulfed tracers
      this.resolveTracersOnGeometryChange();
    }

    // Stage 05.1: Mark ground scalar field dirty for prompt refresh
    this.groundDirty = true;
  }

  public updateWind(
    speed: number,
    directionDeg: number,
    domain?: { width: number; length: number; height: number }
  ): void {
    let windDirChanged = false;

    if (Number.isFinite(speed) && speed > 0) {
      if (Math.abs(speed - this.windSpeed) > 1e-4) {
        this.windSpeed = speed;
        this.groundDirty = true;
      }
    }

    if (Number.isFinite(directionDeg)) {
      const newHeading = (directionDeg * Math.PI) / 180.0;
      if (Math.abs(newHeading - this.windHeadingRad) > 1e-4) {
        this.windHeadingRad = newHeading;
        windDirChanged = true;
        this.groundDirty = true;
      }
    }

    if (windDirChanged) {
      this.refreshObstacleWindScales();
    }

    if (domain) {
      const newHalfX = Math.max(100.0, domain.width / 2.0);
      const newHalfZ = Math.max(100.0, domain.length / 2.0);
      const newHeight = Math.max(40.0, domain.height);
      const domainChanged =
        Math.abs(newHalfX - this.domainHalfX) > 1.0 ||
        Math.abs(newHalfZ - this.domainHalfZ) > 1.0 ||
        Math.abs(newHeight - this.domainHeight) > 1.0;

      this.domainHalfX = newHalfX;
      this.domainHalfZ = newHalfZ;
      this.domainHeight = newHeight;
      this.buildGroundMesh();

      if (domainChanged && this.tracerCount === 0) {
        this.initVisualTracers();
      }

      if (domainChanged) {
        this.groundDirty = true;
        this.groundInitialBuild = true;
        this.updateGroundScalarGrid(0.0, true);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Stage 05.1: Ground Scalar Texture Pipeline (128x128 RG8 Texture)
  // ---------------------------------------------------------------------------

  /**
   * Computes the atmospheric boundary layer wind speed height factor at elevation y.
   * Derives directly from the canonical ABL formulation used in evaluatePreviewVelocityAt.
   */
  public getAblHeightFactor(y: number): number {
    const clampedY = Math.max(0.1, y);
    return Math.min(1.3, Math.max(0.3, Math.pow(clampedY / 15.0, 0.25)));
  }

  /**
   * Evaluates the 128x128 2D pedestrian-level ground scalar preview field (Stage 05.1).
   * Samples the canonical preview velocity field at y = GROUND_SAMPLE_HEIGHT (1.75m),
   * derives normalized horizontal speed ratio Vh / Uref, applies obstacle solid masking,
   * smooths temporally, and uploads to the WebGL2 RG8 texture.
   */
  private updateGroundScalarGrid(dt: number, forceImmediate: boolean = false): void {
    this.groundTimeSinceRefresh += dt;

    // Refresh cadence: 0.08s (~12.5 Hz) during active interaction or dirty state; 0.25s (4 Hz) when idle
    const targetInterval = this.groundDirty ? 0.08 : 0.25;
    if (!forceImmediate && !this.groundDirty && this.groundTimeSinceRefresh < targetInterval) {
      return;
    }

    this.groundTimeSinceRefresh = 0.0;
    this.groundDirty = false;

    const res = GROUND_GRID_RES;
    const halfX = this.domainHalfX;
    const halfZ = this.domainHalfZ;
    const dx = (2.0 * halfX) / res;
    const dz = (2.0 * halfZ) / res;

    const sampleY = GROUND_SAMPLE_HEIGHT;
    const ablFactor = this.getAblHeightFactor(sampleY);
    const uRef = Math.max(0.1, this.windSpeed * ablFactor);
    const fieldTime = this.simTime;
    const fParams = FlowPreviewRenderer._fieldParams;
    const vOut = FlowPreviewRenderer._velOut;
    fParams.speedScale = 1.0;
    fParams.seed = 0.0;

    const target = this.groundScalarTarget;
    const display = this.groundScalarDisplay;
    const texBuf = this.groundTextureBuffer;
    const lambda = this.groundInitialBuild ? 1.0 : 0.30;
    this.groundInitialBuild = false;

    let idx = 0;
    for (let iz = 0; iz < res; iz++) {
      const wz = -halfZ + (iz + 0.5) * dz;
      for (let ix = 0; ix < res; ix++) {
        const wx = -halfX + (ix + 0.5) * dx;

        // 1. Evaluate solid obstacle footprint clearance
        let isSolid = false;
        for (let o = 0; o < this.obstacles.length; o++) {
          const obs = this.obstacles[o];
          if (sampleY > obs.height) continue;
          if (Math.hypot(wx - obs.cx, wz - obs.cz) > obs.radius + 0.5) continue;

          const { clearance } = this.getObstacleClearanceAndNormal(obs, wx, wz);
          if (clearance < 0.0) {
            isSolid = true;
            break;
          }
        }

        // 2. Evaluate preview velocity at pedestrian reference plane
        this.evaluatePreviewVelocityAt(wx, sampleY, wz, fieldTime, fParams, vOut);
        const vh = Math.hypot(vOut.vx, vOut.vz);
        const sRatio = Number.isFinite(vh) ? Math.min(2.0, Math.max(0.0, vh / uRef)) : 1.0;

        target[idx] = sRatio;

        // 3. Temporal exponential smoothing: display = display * (1 - lambda) + target * lambda
        const smoothed = display[idx] * (1.0 - lambda) + sRatio * lambda;
        display[idx] = smoothed;

        // 4. Quantize to RG8: Red = sRatio / 2.0 * 255, Green = solidMask (255 inside, 0 outside)
        const rByte = Math.round(Math.min(1.0, Math.max(0.0, smoothed / 2.0)) * 255);
        const gByte = isSolid ? 255 : 0;

        const byteOffset = idx * 2;
        texBuf[byteOffset] = rByte;
        texBuf[byteOffset + 1] = gByte;

        idx++;
      }
    }

    // 5. Upload sub-image to existing WebGL2 texture
    if (this.gl && this.groundTexture) {
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.groundTexture);
      this.gl.texSubImage2D(
        this.gl.TEXTURE_2D,
        0,
        0,
        0,
        res,
        res,
        this.gl.RG,
        this.gl.UNSIGNED_BYTE,
        texBuf
      );
      this.gl.bindTexture(this.gl.TEXTURE_2D, null);
    }
  }

  // ---------------------------------------------------------------------------
  // Ground Mesh Generation (Subtle Lab Platform)
  // ---------------------------------------------------------------------------

  private buildGroundMesh(): void {
    if (!this.gl || !this.groundVbo) return;

    const vertices: number[] = [];
    const radius = Math.max(this.domainHalfX, this.domainHalfZ) * 1.25;
    const SEGMENTS = 64;

    // Laboratory ground disc on Y = -0.05 (prevent z-fighting with Y=0 buildings)
    const yGround = -0.05;

    // Subtle light architectural ground slab (alabaster tone)
    const centerColor = [0.90, 0.89, 0.87, 0.60]; // soft light warm slab
    const rimColor = [0.94, 0.93, 0.91, 0.0];     // outer edge fades softly to transparent

    for (let i = 0; i < SEGMENTS; i++) {
      const a0 = (i / SEGMENTS) * Math.PI * 2.0;
      const a1 = ((i + 1) / SEGMENTS) * Math.PI * 2.0;

      const x0 = Math.cos(a0) * radius;
      const z0 = Math.sin(a0) * radius;
      const x1 = Math.cos(a1) * radius;
      const z1 = Math.sin(a1) * radius;

      // Triangle 1: center -> p0 -> p1
      vertices.push(
        0, yGround, 0, centerColor[0], centerColor[1], centerColor[2], centerColor[3],
        x0, yGround, z0, rimColor[0], rimColor[1], rimColor[2], rimColor[3],
        x1, yGround, z1, rimColor[0], rimColor[1], rimColor[2], rimColor[3]
      );
    }

    // Directional orientation ring indicator on ground (radius = 65m) - faint slate
    const ringR = 65.0;
    const ringCol = [0.45, 0.55, 0.65, 0.20];
    for (let i = 0; i < SEGMENTS; i++) {
      const a0 = (i / SEGMENTS) * Math.PI * 2.0;
      const a1 = ((i + 1) / SEGMENTS) * Math.PI * 2.0;
      const rx0 = Math.cos(a0) * ringR;
      const rz0 = Math.sin(a0) * ringR;
      const rx1 = Math.cos(a1) * ringR;
      const rz1 = Math.sin(a1) * ringR;

      vertices.push(
        rx0, yGround + 0.01, rz0, ringCol[0], ringCol[1], ringCol[2], ringCol[3],
        rx1, yGround + 0.01, rz1, ringCol[0], ringCol[1], ringCol[2], ringCol[3],
        rx0 * 1.015, yGround + 0.01, rz0 * 1.015, ringCol[0], ringCol[1], ringCol[2], 0.0
      );
    }

    const data = new Float32Array(vertices);
    this.groundVertexCount = vertices.length / 7;

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.groundVbo);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, data, this.gl.STATIC_DRAW);
  }

  // ---------------------------------------------------------------------------
  // Stage 04: Precomputed Obstacle Field Scales & Geometry Change Handling
  // ---------------------------------------------------------------------------

  /**
   * Precomputes wind-aligned projection scales, wake influence extents, and bounding
   * radii for all obstacles upon wind direction or geometry changes (zero per-frame allocations).
   */
  private refreshObstacleWindScales(): void {
    const Ux = Math.sin(this.windHeadingRad);
    const Uz = -Math.cos(this.windHeadingRad);
    const Px = -Uz;
    const Pz = Ux;

    for (let o = 0; o < this.obstacles.length; o++) {
      const obs = this.obstacles[o];
      const { crosswindHalfWidth, streamwiseHalfLength } =
        this.getProjectedObstacleScales(obs, Ux, Uz, Px, Pz);
      obs.wProj = crosswindHalfWidth;
      obs.lProj = streamwiseHalfLength;

      obs.wakeLength = Math.max(20.0, (obs.lProj + obs.wProj) * PREVIEW_MODEL_PARAMS.wakeLengthMultiplier);
      obs.maxUpwindDist = obs.lProj + obs.wProj * 2.8;
      obs.maxCrosswindDist = obs.wProj * 3.8;
      obs.influenceRadiusSq = Math.pow(obs.radius + obs.wakeLength + 10.0, 2);
    }
  }

  /**
   * Preserves ongoing visual tracer population during interactive building dragging/editing.
   * Safely nudges or recycles only tracers directly engulfed by relocated solid mass.
   */
  private resolveTracersOnGeometryChange(): void {
    for (let i = 0; i < this.activeVisualTracerCount; i++) {
      const x = this.tracerX[i];
      const y = this.tracerY[i];
      const z = this.tracerZ[i];

      for (let o = 0; o < this.obstacles.length; o++) {
        const obs = this.obstacles[o];
        if (y >= obs.height) continue;
        if (Math.hypot(x - obs.cx, z - obs.cz) > obs.radius + 1.0) continue;

        const { clearance, nx: wallNx, nz: wallNz } = this.getObstacleClearanceAndNormal(obs, x, z);
        if (clearance < -1.0) {
          this.recycleVisualTracer(i);
          break;
        } else if (clearance < 0.35) {
          this.tracerX[i] += wallNx * (0.35 - clearance);
          this.tracerZ[i] += wallNz * (0.35 - clearance);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Stage 03.4: Persistent Visual Tracer Pool Lifecycle & Advection
  // ---------------------------------------------------------------------------

  /**
   * Initializes the persistent visual tracer pool across stratified 3D domain layers,
   * omnidirectional building approach envelopes, and inter-building gap corridors.
   * Staggers initial tracer ages to eliminate any synchronized birth/death cycle.
   */
  private initVisualTracers(): void {
    let count = 0;
    const maxTracers = MAX_VISUAL_TRACERS;

    const Hx = this.domainHalfX * 0.82;
    const Hz = this.domainHalfZ * 0.82;
    const domainH = this.domainHeight;

    // 4 vertical urban layers:
    // 0: Pedestrian / near-ground canopy (1.8m)
    // 1: Low urban flank / mid-canopy (6.5m)
    // 2: Mid-building / waist shear (14.0m)
    // 3: Upper urban / over-roof plume (min(26.0m, domainH * 0.65))
    const layers = [
      1.8,
      6.5,
      14.0,
      Math.min(26.0, domainH * 0.65)
    ];

    // Part A: Stratified Base Domain Grid (~1,140 tracers)
    const targetPerLayer = 285;
    const area = (2.0 * Hx) * (2.0 * Hz);
    const stepGrid = Math.sqrt(area / targetPerLayer);
    const nx = Math.max(8, Math.floor((2.0 * Hx) / stepGrid));
    const nz = Math.max(8, Math.floor((2.0 * Hz) / stepGrid));
    const dx = (2.0 * Hx) / nx;
    const dz = (2.0 * Hz) / nz;

    for (let l = 0; l < layers.length; l++) {
      const yBase = layers[l];
      for (let ix = 0; ix < nx; ix++) {
        for (let iz = 0; iz < nz; iz++) {
          if (count >= maxTracers - 860) break; // Reserve room for obstacle shells

          const h1 = deterministicHash(ix, iz, l, 101);
          const h2 = deterministicHash(ix, iz, l, 202);
          const h3 = deterministicHash(ix, iz, l, 303);
          const h4 = deterministicHash(ix, iz, l, 404);

          let sx = -Hx + (ix + 0.5) * dx + (h1 - 0.5) * dx * 0.75;
          let sz = -Hz + (iz + 0.5) * dz + (h2 - 0.5) * dz * 0.75;
          let sy = Math.max(0.8, yBase + (h3 - 0.5) * 1.6);

          // Check building interiors and proximity
          let minClearance = 999.0;
          for (let o = 0; o < this.obstacles.length; o++) {
            const obs = this.obstacles[o];
            if (sy >= obs.height) continue;
            const dist = Math.hypot(sx - obs.cx, sz - obs.cz);
            if (dist > obs.radius + 30.0) continue;

            const { clearance, nx: wallNx, nz: wallNz } = this.getObstacleClearanceAndNormal(obs, sx, sz);
            if (clearance < minClearance) minClearance = clearance;

            if (clearance <= 0.0) {
              // Project outward to building perimeter + 1.2m
              sx += wallNx * (-clearance + 1.2);
              sz += wallNz * (-clearance + 1.2);
              minClearance = 1.2;
              break;
            }
          }

          const tier = minClearance < 25.0 ? 1 : 0;
          const baseAlpha = tier === 1 ? 0.48 : 0.36;
          const maxAge = 14.0 + h3 * 14.0; // 14s to 28s randomized max age

          this.tracerX[count] = sx;
          this.tracerY[count] = sy;
          this.tracerZ[count] = sz;
          this.tracerMaxAge[count] = maxAge;
          this.tracerAge[count] = h4 * maxAge; // Desynchronized initial age
          this.tracerBaseAlpha[count] = baseAlpha;
          this.tracerTier[count] = tier;
          this.tracerSpeedScale[count] = 0.94 + h1 * 0.12;
          this.tracerSeed[count] = h2;
          this.tracerSourceType[count] = 0; // Domain inflow
          this.tracerSourceIndex[count] = -1;
          count++;
        }
      }
    }

    // Part B: Obstacle-Targeted Coverage (Omnidirectional Multi-Shells & Inter-Building Gaps)
    if (this.obstacles.length > 0) {
      const obsBudget = Math.min(800, maxTracers - count - 50);
      const quotaPerObs = Math.max(1, Math.floor(obsBudget / this.obstacles.length));

      for (let o = 0; o < this.obstacles.length; o++) {
        if (count >= maxTracers - 60) break;
        const obs = this.obstacles[o];
        let obsSeeds = 0;

        const effectiveH = Math.min(obs.height, 35.0);
        const yHeights = [
          1.8,
          Math.min(effectiveH * 0.45, 12.0),
          Math.min(effectiveH * 0.85, 24.0)
        ];

        // 4 concentric shells around building perimeter:
        // Shell 0 (Facade & corner boundary layer): clearance 1.8m
        // Shell 1 (Flank separation & near wake cavity): clearance 5.5m
        // Shell 2 (Wake deficit & approach deflection): clearance 14.0m
        // Shell 3 (Wake recovery & outer shear): clearance 28.0m
        const clearances = [1.8, 5.5, 14.0, 28.0];
        const shellTiers: (0 | 1 | 2)[] = [2, 2, 2, 1];
        const shellAlphas = [0.62, 0.58, 0.50, 0.44];

        const ANGLES_COUNT = 16;
        for (let sh = 0; sh < clearances.length && obsSeeds < quotaPerObs; sh++) {
          const clr = clearances[sh];
          const tier = shellTiers[sh];
          const baseAlpha = shellAlphas[sh];

          for (let yh = 0; yh < yHeights.length && obsSeeds < quotaPerObs; yh++) {
            const sy = yHeights[yh];

            for (let a = 0; a < ANGLES_COUNT && obsSeeds < quotaPerObs; a++) {
              if (count >= maxTracers - 40) break;
              const theta = (a / ANGLES_COUNT) * Math.PI * 2.0;

              let sx = 0;
              let sz = 0;

              if (obs.isCircle) {
                const r = obs.circleRadius + clr;
                sx = obs.cx + Math.cos(theta) * r;
                sz = obs.cz + Math.sin(theta) * r;
              } else {
                const localHw = obs.hw + clr;
                const localHd = obs.hd + clr;
                const ct = Math.cos(theta);
                const st = Math.sin(theta);
                const scale = 1.0 / Math.max(Math.abs(ct) / localHw, Math.abs(st) / localHd);
                const lx = ct * scale;
                const lz = st * scale;
                sx = obs.cx + (lx * obs.cosR - lz * obs.sinR);
                sz = obs.cz + (lx * obs.sinR + lz * obs.cosR);
              }

              const h1 = deterministicHash(o, sh * 10 + yh, a, 801);
              const h2 = deterministicHash(o, sh * 10 + yh, a, 802);
              const maxAge = 12.0 + h2 * 12.0; // 12s to 24s randomized max age

              this.tracerX[count] = sx + (h1 - 0.5) * 1.2;
              this.tracerY[count] = sy;
              this.tracerZ[count] = sz + (h2 - 0.5) * 1.2;
              this.tracerMaxAge[count] = maxAge;
              this.tracerAge[count] = h1 * maxAge; // Desynchronized initial age
              this.tracerBaseAlpha[count] = baseAlpha;
              this.tracerTier[count] = tier;
              this.tracerSpeedScale[count] = 0.95 + h2 * 0.10;
              this.tracerSeed[count] = h1;
              this.tracerSourceType[count] = 1; // Obstacle-targeted
              this.tracerSourceIndex[count] = o;
              count++;
              obsSeeds++;
            }
          }
        }
      }

      // Inter-Building Gaps / Narrow Passages (Section 22)
      for (let i = 0; i < this.obstacles.length - 1; i++) {
        for (let j = i + 1; j < this.obstacles.length; j++) {
          if (count >= maxTracers - 24) break;
          const o1 = this.obstacles[i];
          const o2 = this.obstacles[j];
          const distCenters = Math.hypot(o1.cx - o2.cx, o1.cz - o2.cz);
          const gap = distCenters - (o1.radius + o2.radius);
          if (gap > 0.5 && gap < 45.0) {
            const minH = Math.min(o1.height, o2.height);
            const gapY = [1.8, Math.min(minH * 0.45, 10.0), Math.min(minH * 0.8, 18.0)];

            for (let gy = 0; gy < gapY.length && count < maxTracers; gy++) {
              for (let frac = 0.25; frac <= 0.75; frac += 0.25) {
                if (count >= maxTracers) break;
                const gx = o1.cx + (o2.cx - o1.cx) * frac;
                const gz = o1.cz + (o2.cz - o1.cz) * frac;
                const h1 = deterministicHash(i, j, gy, Math.floor(frac * 100));
                const maxAge = 10.0 + h1 * 12.0;

                this.tracerX[count] = gx;
                this.tracerY[count] = gapY[gy];
                this.tracerZ[count] = gz;
                this.tracerMaxAge[count] = maxAge;
                this.tracerAge[count] = h1 * maxAge; // Desynchronized initial age
                this.tracerBaseAlpha[count] = 0.62;
                this.tracerTier[count] = 2; // High priority gap
                this.tracerSpeedScale[count] = 1.0;
                this.tracerSeed[count] = h1;
                this.tracerSourceType[count] = 2; // Gap-targeted
                this.tracerSourceIndex[count] = i;
                count++;
              }
            }
          }
        }
      }
    }

    // Part C: Ensure entire maximum preallocated pool (0 .. maxTracers - 1) is populated
    while (count < maxTracers) {
      this.spawnStaggeredTracer(count, count * 31 + 101);
      count++;
    }

    this.tracerCount = count;
    if (this.activeVisualTracerCount === 0) {
      this.activeVisualTracerCount = DEFAULT_VISUAL_TRACERS;
    }
  }

  /**
   * Initializes or refreshes a single visual tracer slot with naturally staggered placement,
   * desynchronized age, and appropriate source kind (domain inflow, obstacle shell, or gap corridor).
   */
  private spawnStaggeredTracer(index: number, seedOffset: number): void {
    const Hx = this.domainHalfX * 0.82;
    const Hz = this.domainHalfZ * 0.82;
    const domainH = this.domainHeight;

    const layers = [1.8, 6.5, 14.0, Math.min(26.0, domainH * 0.65)];
    const h1 = deterministicHash(index, seedOffset, 1, 501);
    const h2 = deterministicHash(index, seedOffset, 2, 602);
    const h3 = deterministicHash(index, seedOffset, 3, 703);
    const h4 = deterministicHash(index, seedOffset, 4, 804);

    let sx = 0;
    let sy = 1.8;
    let sz = 0;
    let tier: 0 | 1 | 2 = 0;
    let baseAlpha = 0.36;
    let srcType = 0;
    let srcIdx = -1;

    if (this.obstacles.length > 1 && index % 10 === 0) {
      // Gap-targeted tracer
      srcType = 2;
      srcIdx = (Math.floor(index / 10)) % this.obstacles.length;
      const o1 = this.obstacles[srcIdx];
      const o2 = this.obstacles[(srcIdx + 1) % this.obstacles.length];
      const frac = 0.25 + h1 * 0.5;
      sx = o1.cx + (o2.cx - o1.cx) * frac + (h2 - 0.5) * 1.5;
      sz = o1.cz + (o2.cz - o1.cz) * frac + (h3 - 0.5) * 1.5;
      const minH = Math.min(o1.height, o2.height);
      const gapLayers = [1.8, Math.min(minH * 0.45, 10.0), Math.min(minH * 0.8, 18.0)];
      sy = gapLayers[Math.floor(h4 * gapLayers.length)] + (h1 - 0.5) * 0.8;
      tier = 2;
      baseAlpha = 0.62;
    } else if (this.obstacles.length > 0 && index % 3 !== 0) {
      // Obstacle-targeted tracer
      srcType = 1;
      srcIdx = index % this.obstacles.length;
      const obs = this.obstacles[srcIdx];
      const clearances = [1.8, 5.5, 14.0, 28.0];
      const shellTiers: (0 | 1 | 2)[] = [2, 2, 2, 1];
      const shellAlphas = [0.62, 0.58, 0.50, 0.44];
      const sh = Math.floor(h1 * clearances.length);
      const clr = clearances[sh];
      tier = shellTiers[sh];
      baseAlpha = shellAlphas[sh];

      const theta = h2 * Math.PI * 2.0;
      if (obs.isCircle) {
        const r = obs.circleRadius + clr;
        sx = obs.cx + Math.cos(theta) * r;
        sz = obs.cz + Math.sin(theta) * r;
      } else {
        const localHw = obs.hw + clr;
        const localHd = obs.hd + clr;
        const ct = Math.cos(theta);
        const st = Math.sin(theta);
        const scale = 1.0 / Math.max(Math.abs(ct) / localHw, Math.abs(st) / localHd);
        const lx = ct * scale;
        const lz = st * scale;
        sx = obs.cx + (lx * obs.cosR - lz * obs.sinR);
        sz = obs.cz + (lx * obs.sinR + lz * obs.cosR);
      }
      const effectiveH = Math.min(obs.height, 35.0);
      const obsY = [1.8, Math.min(effectiveH * 0.45, 12.0), Math.min(effectiveH * 0.85, 24.0)];
      sy = obsY[Math.floor(h3 * obsY.length)] + (h4 - 0.5) * 0.8;
    } else {
      // Stratified domain grid
      srcType = 0;
      srcIdx = -1;
      sx = -Hx + h1 * 2.0 * Hx;
      sz = -Hz + h2 * 2.0 * Hz;
      sy = layers[Math.floor(h3 * layers.length)] + (h4 - 0.5) * 1.5;
      tier = 0;
      baseAlpha = 0.36;
    }

    // Solid obstacle clearance enforcement
    for (let o = 0; o < this.obstacles.length; o++) {
      const obs = this.obstacles[o];
      if (sy >= obs.height) continue;
      if (Math.hypot(sx - obs.cx, sz - obs.cz) > obs.radius + 30.0) continue;
      const { clearance, nx: wallNx, nz: wallNz } = this.getObstacleClearanceAndNormal(obs, sx, sz);
      if (clearance <= 0.0) {
        sx += wallNx * (-clearance + 1.2);
        sz += wallNz * (-clearance + 1.2);
        break;
      }
    }

    const maxAge = 12.0 + h3 * 14.0;
    this.tracerX[index] = sx;
    this.tracerY[index] = Math.max(0.8, sy);
    this.tracerZ[index] = sz;
    this.tracerMaxAge[index] = maxAge;
    this.tracerAge[index] = h4 * maxAge; // Desynchronized age
    this.tracerBaseAlpha[index] = baseAlpha;
    this.tracerTier[index] = tier;
    this.tracerSpeedScale[index] = 0.94 + h1 * 0.12;
    this.tracerSeed[index] = h2;
    this.tracerSourceType[index] = srcType;
    this.tracerSourceIndex[index] = srcIdx;
  }

  /**
   * Dynamically adjusts the active visual tracer pool count (300 to 5,000).
   * - Increasing density initializes ONLY newly activated slots with naturally staggered placement.
   * - Decreasing density skips inactive slots in advection and rendering loops with zero memory allocations.
   * - Existing active tracers remain 100% undisturbed and continuously advected.
   */
  public setFlowDensity(density: number): void {
    const clamped = Math.max(300, Math.min(MAX_VISUAL_TRACERS, Math.round(density)));
    if (clamped === this.activeVisualTracerCount) return;

    const prevCount = this.activeVisualTracerCount;
    this.activeVisualTracerCount = clamped;

    if (clamped > prevCount) {
      // Safely initialize newly activated tracers
      for (let i = prevCount; i < clamped; i++) {
        this.spawnStaggeredTracer(i, i * 17 + 911);
      }
    }
  }

  public get activeTracerCount(): number {
    return this.activeVisualTracerCount;
  }

  /**
   * Recycles an individual visual tracer upon domain boundary exit, lifetime expiry,
   * or deep obstacle penetration. Spawns asynchronously into its respective upstream
   * inflow boundary or upwind building approach envelope.
   */
  private recycleVisualTracer(index: number): void {
    const Ux = Math.sin(this.windHeadingRad);
    const Uz = -Math.cos(this.windHeadingRad);
    const Px = -Uz;
    const Pz = Ux;

    const srcType = this.tracerSourceType[index];
    const srcIdx = this.tracerSourceIndex[index];

    let rx = 0;
    let ry = 1.8;
    let rz = 0;

    if (srcType === 1 && this.obstacles.length > 0) {
      // Obstacle-targeted tracer: respawn at upwind approach envelope of assigned obstacle
      const obs = this.obstacles[Math.abs(srcIdx) % this.obstacles.length];
      const distUpwind = obs.radius + 6.0 + Math.random() * 26.0;
      const spanCrosswind = (Math.random() - 0.5) * (obs.radius * 2.2 + 8.0);
      rx = obs.cx - Ux * distUpwind + Px * spanCrosswind;
      rz = obs.cz - Uz * distUpwind + Pz * spanCrosswind;
      ry = Math.max(1.2, Math.random() * Math.min(obs.height * 0.9, 28.0));

      // Push outside if inside any other obstacle
      for (let o = 0; o < this.obstacles.length; o++) {
        const other = this.obstacles[o];
        if (ry >= other.height) continue;
        if (Math.hypot(rx - other.cx, rz - other.cz) > other.radius + 0.5) continue;
        const { clearance, nx, nz } = this.getObstacleClearanceAndNormal(other, rx, rz);
        if (clearance <= 0.0) {
          rx += nx * (-clearance + 1.5);
          rz += nz * (-clearance + 1.5);
        }
      }
    } else if (srcType === 2 && this.obstacles.length > 1) {
      // Gap-targeted tracer: respawn upwind of inter-building gap
      const o1 = this.obstacles[Math.abs(srcIdx) % this.obstacles.length];
      const o2 = this.obstacles[(Math.abs(srcIdx) + 1) % this.obstacles.length];
      const gx = (o1.cx + o2.cx) * 0.5;
      const gz = (o1.cz + o2.cz) * 0.5;
      const distUpwind = 12.0 + Math.random() * 18.0;
      const span = (Math.random() - 0.5) * 6.0;
      rx = gx - Ux * distUpwind + Px * span;
      rz = gz - Uz * distUpwind + Pz * span;
      ry = 1.5 + Math.random() * Math.min(Math.min(o1.height, o2.height) * 0.8, 16.0);
    } else {
      // Domain-level inflow tracer: respawn on windward boundary faces
      const Hx = this.domainHalfX;
      const Hz = this.domainHalfZ;
      const absUx = Math.abs(Ux);
      const absUz = Math.abs(Uz);
      const fluxX = Hz * absUx;
      const fluxZ = Hx * absUz;
      const totalFlux = fluxX + fluxZ;
      const pickX = totalFlux > 0.0001 ? Math.random() * totalFlux < fluxX : Math.random() < 0.5;
      const margin = 1.5 + Math.random() * 4.0;

      if (pickX) {
        rx = Ux >= 0 ? -Hx - margin : Hx + margin;
        rz = (-Hz + Math.random() * 2.0 * Hz) * 0.96;
      } else {
        rz = Uz >= 0 ? -Hz - margin : Hz + margin;
        rx = (-Hx + Math.random() * 2.0 * Hx) * 0.96;
      }
      const layerY = [1.8, 6.5, 14.0, Math.min(26.0, this.domainHeight * 0.65)];
      ry = Math.max(0.8, layerY[Math.floor(Math.random() * layerY.length)] + (Math.random() - 0.5) * 1.5);
    }

    this.tracerX[index] = rx;
    this.tracerY[index] = ry;
    this.tracerZ[index] = rz;
    this.tracerAge[index] = 0;
    this.tracerMaxAge[index] = 12.0 + Math.random() * 16.0;
    this.tracerSpeedScale[index] = 0.94 + Math.random() * 0.12;
    this.tracerSeed[index] = Math.random();
  }

  /**
   * Advects all visual tracers continuously through the canonical velocity field.
   * Motion scales directly with velocity magnitude. Zero birth/death envelopes or phase loops.
   */
  private updateVisualTracers(dt: number): void {
    const Ux = Math.sin(this.windHeadingRad);
    const Uz = -Math.cos(this.windHeadingRad);

    const Hx = this.domainHalfX;
    const Hz = this.domainHalfZ;
    const exitMargin = 4.0;
    const vOut = FlowPreviewRenderer._velOut;
    const fParams = FlowPreviewRenderer._fieldParams;
    const advectionScale = 1.6;

    // Stage 04: Numerical substepping for large frame times to prevent high-speed tunneling
    const subSteps = dt > 0.024 ? 2 : 1;
    const subDt = dt / subSteps;

    for (let i = 0; i < this.activeVisualTracerCount; i++) {
      let x = this.tracerX[i];
      let y = this.tracerY[i];
      let z = this.tracerZ[i];

      fParams.speedScale = this.tracerSpeedScale[i];
      fParams.seed = this.tracerSeed[i];

      let needsRecycle = false;

      for (let s = 0; s < subSteps; s++) {
        // 1. Evaluate instantaneous velocity at tracer position via canonical field evaluator
        this.evaluatePreviewVelocityAt(x, y, z, this.simTime, fParams, vOut);

        // 2. Advect tracer position along local velocity field
        x += vOut.vx * subDt * advectionScale;
        y = Math.max(0.5, y + vOut.vy * subDt * advectionScale);
        z += vOut.vz * subDt * advectionScale;

        // 3. Solid obstacle penetration avoidance & surface sliding
        for (let o = 0; o < this.obstacles.length; o++) {
          const obs = this.obstacles[o];
          if (y >= obs.height) continue;
          if (Math.hypot(x - obs.cx, z - obs.cz) > obs.radius + 1.0) continue;

          const { clearance, nx: wallNx, nz: wallNz } = this.getObstacleClearanceAndNormal(obs, x, z);
          if (clearance < -1.0) {
            // Deep penetration inside obstacle mass -> recycle
            needsRecycle = true;
            break;
          } else if (clearance < 0.35) {
            // Near-surface contact: gently nudge outward along geometry surface normal
            const push = 0.35 - clearance;
            x += wallNx * push;
            z += wallNz * push;
          }
        }

        if (needsRecycle) break;
      }

      this.tracerX[i] = x;
      this.tracerY[i] = y;
      this.tracerZ[i] = z;
      this.tracerAge[i] += dt;

      // 4. Downstream domain boundary exit & lifetime expiry checks
      const exitedDownstreamX = Math.abs(Ux) > 0.05 && (Ux > 0 ? x > Hx + exitMargin : x < -Hx - exitMargin);
      const exitedDownstreamZ = Math.abs(Uz) > 0.05 && (Uz > 0 ? z > Hz + exitMargin : z < -Hz - exitMargin);
      const outOfBounds = Math.abs(x) > Hx + 25.0 || Math.abs(z) > Hz + 25.0 || y > this.domainHeight * 0.95 || y < 0.2;
      const expired = this.tracerAge[i] >= this.tracerMaxAge[i];

      if (needsRecycle || exitedDownstreamX || exitedDownstreamZ || outOfBounds || expired) {
        this.recycleVisualTracer(i);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Particle System Simulation
  // ---------------------------------------------------------------------------

  private initParticles(): void {
    this.particles = [];
    for (let i = 0; i < this.PARTICLE_COUNT; i++) {
      const p = this.createParticle(false);
      this.particles.push(p);
    }
  }

  private createParticle(atInflow: boolean): Particle {
    const p: Particle = {
      x: 0,
      y: 0,
      z: 0,
      vx: 0,
      vy: 0,
      vz: 0,
      speed: this.windSpeed,
      age: 0,
      maxAge: 6000 + Math.floor(Math.random() * 2000),
      seed: Math.random(),
      speedScale: 0.92 + Math.random() * 0.16,
      alphaScale: 0.24 + Math.random() * 0.20
    };
    this.resetParticle(p, atInflow);
    return p;
  }

  private resetParticle(p: Particle, atInflow: boolean): void {
    const Ux = Math.sin(this.windHeadingRad);
    const Uz = -Math.cos(this.windHeadingRad);

    const Hx = this.domainHalfX;
    const Hz = this.domainHalfZ;
    const Hy = this.domainHeight;

    let x = 0;
    let z = 0;
    const y = 1.2 + Math.random() * Math.min(38.0, Hy * 0.6);

    if (atInflow) {
      // Inflow boundary faces of the computational bounding box [-Hx, Hx] x [-Hz, Hz]
      const absUx = Math.abs(Ux);
      const absUz = Math.abs(Uz);
      const fluxX = Hz * absUx;
      const fluxZ = Hx * absUz;
      const totalFlux = fluxX + fluxZ;

      const pickX = totalFlux > 0.0001 ? Math.random() * totalFlux < fluxX : Math.random() < 0.5;
      const margin = 1.5 + Math.random() * 4.0;

      if (pickX) {
        // Enters through windward X boundary
        x = Ux >= 0 ? -Hx - margin : Hx + margin;
        z = (-Hz + Math.random() * 2.0 * Hz) * 0.98;
      } else {
        // Enters through windward Z boundary
        z = Uz >= 0 ? -Hz - margin : Hz + margin;
        x = (-Hx + Math.random() * 2.0 * Hx) * 0.98;
      }
    } else {
      // Uniform initial distribution throughout the computational box on startup
      x = (-Hx + Math.random() * 2.0 * Hx) * 0.95;
      z = (-Hz + Math.random() * 2.0 * Hz) * 0.95;
    }

    p.x = x;
    p.y = y;
    p.z = z;
    p.seed = Math.random();
    p.speedScale = 0.92 + Math.random() * 0.16;
    p.alphaScale = 0.24 + Math.random() * 0.20;
    p.vx = Ux * this.windSpeed * p.speedScale;
    p.vy = 0;
    p.vz = Uz * this.windSpeed * p.speedScale;
    p.speed = this.windSpeed * p.speedScale;
    p.age = 0;
    p.maxAge = 6000 + Math.floor(Math.random() * 2000);
  }

  // ---------------------------------------------------------------------------
  // Geometry & Flow Field Helpers (Stage 02 Urban Obstacle Upgrade)
  // ---------------------------------------------------------------------------

  /**
   * Computes signed/unsigned 2D horizontal clearance and world outward surface normal
   * from an obstacle's oriented footprint (Section B & M).
   * - clearance > 0: outside obstacle
   * - clearance <= 0: inside obstacle (penetration depth)
   */
  private getObstacleClearanceAndNormal(
    obs: ObstacleData,
    px: number,
    pz: number
  ): { clearance: number; nx: number; nz: number } {
    const dx = px - obs.cx;
    const dz = pz - obs.cz;

    if (obs.isCircle) {
      const dist = Math.hypot(dx, dz);
      if (dist < 1e-4) {
        return { clearance: -obs.circleRadius, nx: 1.0, nz: 0.0 };
      }
      return {
        clearance: dist - obs.circleRadius,
        nx: dx / dist,
        nz: dz / dist
      };
    }

    // Un-rotate into local rectangular footprint coordinates (Section A)
    const lx = dx * obs.cosR + dz * obs.sinR;
    const lz = -dx * obs.sinR + dz * obs.cosR;

    const qx = Math.abs(lx) - obs.hw;
    const qz = Math.abs(lz) - obs.hd;

    const sgnX = lx >= 0 ? 1.0 : -1.0;
    const sgnZ = lz >= 0 ? 1.0 : -1.0;

    let localNx = 0.0;
    let localNz = 0.0;
    let clearance = 0.0;

    if (qx > 0 || qz > 0) {
      // Outside the rectangular footprint
      const ox = Math.max(0.0, qx);
      const oz = Math.max(0.0, qz);
      clearance = Math.hypot(ox, oz);

      if (ox > 0 && oz > 0) {
        // Corner region: radial vector from corner vertex
        localNx = (ox / clearance) * sgnX;
        localNz = (oz / clearance) * sgnZ;
      } else if (qx > qz) {
        // Closer to local X face (east/west face in local frame)
        localNx = sgnX;
        localNz = 0.0;
      } else {
        // Closer to local Z face (north/south face in local frame)
        localNx = 0.0;
        localNz = sgnZ;
      }
    } else {
      // Inside the footprint: signed penetration distance <= 0
      clearance = Math.max(qx, qz);
      if (qx > qz) {
        localNx = sgnX;
        localNz = 0.0;
      } else {
        localNx = 0.0;
        localNz = sgnZ;
      }
    }

    // Rotate local normal back to world space
    const worldNx = localNx * obs.cosR - localNz * obs.sinR;
    const worldNz = localNx * obs.sinR + localNz * obs.cosR;

    return { clearance, nx: worldNx, nz: worldNz };
  }

  /**
   * Computes projected building dimensions along streamwise (U) and crosswind (P) directions (Section H).
   */
  private getProjectedObstacleScales(
    obs: ObstacleData,
    Ux: number,
    Uz: number,
    Px: number,
    Pz: number
  ): { crosswindHalfWidth: number; streamwiseHalfLength: number } {
    if (obs.isCircle) {
      return {
        crosswindHalfWidth: obs.circleRadius,
        streamwiseHalfLength: obs.circleRadius
      };
    }

    // Local unit axes in world coordinates:
    // eX = (cosR, sinR), eZ = (-sinR, cosR)
    // Crosswind projection: hw * |eX · P| + hd * |eZ · P|
    const dotPX = obs.cosR * Px + obs.sinR * Pz;
    const dotPZ = -obs.sinR * Px + obs.cosR * Pz;
    const crosswindHalfWidth = Math.max(1.5, obs.hw * Math.abs(dotPX) + obs.hd * Math.abs(dotPZ));

    // Streamwise projection: hw * |eX · U| + hd * |eZ · U|
    const dotUX = obs.cosR * Ux + obs.sinR * Uz;
    const dotUZ = -obs.sinR * Ux + obs.cosR * Uz;
    const streamwiseHalfLength = Math.max(1.5, obs.hw * Math.abs(dotUX) + obs.hd * Math.abs(dotUZ));

    return { crosswindHalfWidth, streamwiseHalfLength };
  }

  // ---------------------------------------------------------------------------
  // Canonical Preview Velocity Field Evaluator (Stage 01 + Stage 02 Urban Upgrade)
  // ---------------------------------------------------------------------------

  /**
   * Evaluates the preview velocity vector (vx, vy, vz) at an arbitrary 3D position (x, y, z).
   *
   * Stage 02 Upgrade:
   * - Geometry-aware oriented building footprints & clearances (no axis-aligned assumptions).
   * - Order-independent, commutative obstacle response evaluation.
   * - Continuous windward split transition across centerline (tanh).
   * - Smooth, bounded flank acceleration without runaway compounding.
   * - Geometry-scaled wake with smooth onset, lateral profile, expanding width, and downstream recovery.
   * - Spatially localized, coherent near-wake vortical recirculation tied to building geometry and wind speed.
   * - Smooth vertical roof envelope replacing hard height steps.
   * - Strict numerical safety and bounded velocity limits.
   */
  public evaluatePreviewVelocityAt(
    x: number,
    y: number,
    z: number,
    time: number = this.simTime,
    params?: { speedScale?: number; seed?: number },
    out?: { vx: number; vy: number; vz: number }
  ): { vx: number; vy: number; vz: number } {
    const Ux = Math.sin(this.windHeadingRad);
    const Uz = -Math.cos(this.windHeadingRad);
    const Px = -Uz;
    const Pz = Ux;

    const speedScale = params?.speedScale ?? 1.0;
    const seed = params?.seed ?? 0.0;

    // Vertical atmospheric boundary layer wind speed gradient (Preserved Section P)
    const heightFactor = this.getAblHeightFactor(y);
    const clampedY = Math.max(0.1, y);
    const baseSpeed = Math.max(0.1, this.windSpeed * speedScale * heightFactor);

    // Ambient base velocity
    let vxAmb = Ux * baseSpeed;
    let vyAmb = 0.0;
    let vzAmb = Uz * baseSpeed;

    // Multi-scale atmospheric harmonic meandering (air parcel turbulence)
    const w1 = Math.sin(time * 0.75 + x * 0.018 + z * 0.015 + seed * 6.28);
    const w2 = Math.cos(time * 1.4 + x * 0.038 - z * 0.032 + clampedY * 0.09);
    const w3 = Math.sin(time * 2.2 + z * 0.07 + x * 0.05);
    const lateralDrift = (w1 * 0.08 + w2 * 0.045 + w3 * 0.02) * baseSpeed;
    const verticalDrift = Math.sin(time * 0.7 + x * 0.025 + z * 0.025 + seed * 3.14) * 0.025 * baseSpeed;

    vxAmb += Px * lateralDrift;
    vzAmb += Pz * lateralDrift;
    vyAmb += verticalDrift;

    if (this.obstacles.length === 0) {
      if (out) { out.vx = vxAmb; out.vy = vyAmb; out.vz = vzAmb; return out; }
      return { vx: vxAmb, vy: vyAmb, vz: vzAmb };
    }

    // Order-independent accumulators across all obstacles (Section K)
    let totalWakeFactor = 1.0; // product of (1 - D_i), strictly bounded in [0, 1]
    let sumFlankAccel = 0.0;    // sum of delta_a_i
    let steerVx = 0.0;          // sum of vector lateral / stagnation / vortex deflections (m/s)
    let steerVz = 0.0;
    let sumVy = 0.0;            // sum of vertical diversion (m/s)

    for (let o = 0; o < this.obstacles.length; o++) {
      const obs = this.obstacles[o];

      // Smooth vertical envelope (Section J):
      // For y <= obs.height: vertEnv = 1.0;
      // For y > obs.height: smooth Gaussian decay without abrupt cliff
      let vertEnv = 1.0;
      if (y > obs.height) {
        const sigmaY = Math.max(3.0, obs.height * 0.25 * PREVIEW_MODEL_PARAMS.verticalClearanceScale);
        const dy = y - obs.height;
        vertEnv = Math.exp(-(dy * dy) / (2.0 * sigmaY * sigmaY));
      }
      if (vertEnv < 0.005) continue; // Cleanly above obstacle influence zone

      // Fast early bounding circle rejection (Stage 04 optimization)
      const dx = x - obs.cx;
      const dz = z - obs.cz;
      if (dx * dx + dz * dz > obs.influenceRadiusSq) {
        continue;
      }

      // Precomputed characteristic projected building dimensions (Stage 04 optimization)
      const Wproj = obs.wProj;
      const Lproj = obs.lProj;
      const wakeLength = obs.wakeLength;
      const maxUpwindDist = obs.maxUpwindDist;
      const maxCrosswindDist = obs.maxCrosswindDist;

      // Wind-aligned coordinates (Section C):
      // s = streamwise (positive = downwind/leeward, negative = upwind/windward)
      // n = crosswind (lateral offset from obstacle center line)
      const s = dx * Ux + dz * Uz;
      const n = dx * Px + dz * Pz;

      if (s < -maxUpwindDist || s > wakeLength || Math.abs(n) > maxCrosswindDist + s * PREVIEW_MODEL_PARAMS.wakeExpansionRate) {
        continue;
      }

      // Obstacle clearance and outward surface normal (Section B)
      const { clearance, nx: wallNx, nz: wallNz } = this.getObstacleClearanceAndNormal(obs, x, z);

      // 1. Windward Deflection & Stagnation (Sections D & E)
      if (s < Lproj * 0.5) {
        const splitWidth = Math.max(PREVIEW_MODEL_PARAMS.minSplitWidth, Wproj * PREVIEW_MODEL_PARAMS.splitWidthScale);
        // Continuous bounded directional transition across n = 0 (tanh)
        const split = Math.tanh(n / splitWidth);

        // Clearance decay
        const clearScale = Math.max(3.0, Wproj * 1.2);
        const clearWeight = Math.exp(-Math.max(0.0, clearance) / clearScale);

        // Streamwise approach weighting
        const upwindWeight = smoothstep(-maxUpwindDist, -Lproj * 0.2, s) * (1.0 - smoothstep(-Lproj * 0.1, Lproj * 0.5, s));
        const deflWeight = clearWeight * upwindWeight * PREVIEW_MODEL_PARAMS.deflectionStrength * vertEnv;

        // Add lateral deflection along crosswind vector P
        steerVx += Px * (split * deflWeight * baseSpeed);
        steerVz += Pz * (split * deflWeight * baseSpeed);

        // Stagnation redirection (Section E):
        // Redirection of incoming velocity toward tangential flow along nearest facade
        const inward = -(Ux * wallNx + Uz * wallNz);
        if (inward > 0.0 && clearance < Wproj * 1.6) {
          const stagDistScale = Math.max(2.0, Wproj * 0.85);
          const stagWeight = Math.exp(-Math.max(0.0, clearance) / stagDistScale) *
            inward * PREVIEW_MODEL_PARAMS.stagnationStrength * vertEnv * (1.0 - smoothstep(0.0, Lproj * 0.5, s));

          steerVx += wallNx * (stagWeight * baseSpeed);
          steerVz += wallNz * (stagWeight * baseSpeed);
        }
      }

      // 2. Smooth Flank Acceleration (Section F)
      const sigmaS = Math.max(2.5, Lproj * PREVIEW_MODEL_PARAMS.flankStreamwiseSigma);
      const flankStreamFactor = Math.exp(-(s * s) / (2.0 * sigmaS * sigmaS));

      const flankOffset = Math.abs(n) - Wproj;
      const flankCrossFactor = smoothstep(-Wproj * 0.4, 0.0, flankOffset) *
        Math.exp(-(Math.max(0.0, flankOffset) * Math.max(0.0, flankOffset)) / (2.0 * Wproj * Wproj * 0.65));

      const flankContribution = flankStreamFactor * flankCrossFactor * PREVIEW_MODEL_PARAMS.flankAccelMax * vertEnv;
      sumFlankAccel += flankContribution;

      // 3. Wake Model: Downstream Deficit & Recirculation (Sections G, H, I)
      if (s > 0.0) {
        const localWakeWidth = Wproj + s * PREVIEW_MODEL_PARAMS.wakeExpansionRate;

        const wakeOnset = smoothstep(0.0, Math.max(1.5, Lproj * 0.4), s);
        const wakeDownstreamDecay = Math.exp(-Math.pow(s / wakeLength, PREVIEW_MODEL_PARAMS.wakeDecayPower));
        const wakeLateralDecay = Math.exp(-(n * n) / (2.0 * localWakeWidth * localWakeWidth));

        const wakeDeficit_i = wakeOnset * wakeDownstreamDecay * wakeLateralDecay * PREVIEW_MODEL_PARAMS.wakeDeficitMax * vertEnv;
        // Bounded deficit contribution (capped at 0.70)
        const cappedDeficit = Math.min(0.70, Math.max(0.0, wakeDeficit_i));
        totalWakeFactor *= (1.0 - cappedDeficit);

        // Coherent Near-Wake Vortical Recirculation (Section I)
        if (s < wakeLength * 0.5) {
          const vortexOnset = smoothstep(0.0, Math.max(1.5, Lproj * 0.5), s);
          const vortexDecay = Math.exp(-s / (wakeLength * 0.28)) * wakeLateralDecay * vertEnv;

          // Counter-rotating vortex pair behind building flanks
          const coreS = Lproj + Wproj * 0.5;
          const coreN = Wproj * 0.6;
          const ds = s - coreS;
          const r0 = Math.max(2.0, PREVIEW_MODEL_PARAMS.vortexCoreRadius);

          // Strouhal vortex shedding temporal scaling: f ~ St * U / L
          const charLength = Math.max(3.0, 2.0 * Wproj);
          const fShed = PREVIEW_MODEL_PARAMS.strouhalNumber * (baseSpeed / charLength);
          const vortexPhase = 2.0 * Math.PI * fShed * time + (obs.cx * 0.03 + obs.cz * 0.03);

          let lobeSumVx = 0.0;
          let lobeSumVz = 0.0;

          // Two counter-rotating finite-core vortex lobes (k = +1 right flank, k = -1 left flank)
          for (const k of [1.0, -1.0]) {
            const dn = n - k * coreN;
            const r = Math.hypot(ds, dn);
            // Bounded Lamb-Oseen profile: peak 1.0 at r = r0, zero at origin, no singularity
            const profile = (2.0 * r0 * r) / (r * r + r0 * r0 + 1e-4);

            // Counter-rotating tangential direction in (s, n) basis
            const invR = 1.0 / Math.max(0.5, r);
            const tanS = -k * dn * invR;
            const tanN = k * ds * invR;

            // Phase oscillation: alternating coherent shedding
            const osc = 0.85 + 0.30 * Math.sin(vortexPhase + k * 1.57);

            // Transform tangential vector into world coordinates
            const tanWorldX = tanS * Ux + tanN * Px;
            const tanWorldZ = tanS * Uz + tanN * Pz;

            lobeSumVx += tanWorldX * profile * osc;
            lobeSumVz += tanWorldZ * profile * osc;
          }

          const vortexMag = vortexOnset * vortexDecay * PREVIEW_MODEL_PARAMS.vortexStrengthMax * baseSpeed;
          steerVx += lobeSumVx * 0.5 * vortexMag;
          steerVz += lobeSumVz * 0.5 * vortexMag;
        }
      }

      // 4. Roof / Vertical Diversion (Section J)
      if (s < 0.0) {
        // Upwash on windward approach over front facade
        const upwashApproach = smoothstep(-maxUpwindDist * 0.8, 0.0, s);
        const upwashClear = Math.exp(-Math.max(0.0, clearance) / Math.max(2.5, Wproj * 0.8));
        const upwashHeightFactor = smoothstep(obs.height * 0.3, obs.height * 1.1, y);
        const upwash = upwashApproach * upwashClear * upwashHeightFactor *
          PREVIEW_MODEL_PARAMS.roofLiftStrength * baseSpeed * vertEnv;
        sumVy += upwash;
      } else {
        // Downwash in near wake behind roof separation
        const downwashDecay = Math.exp(-s / (wakeLength * 0.25)) * Math.exp(-(n * n) / (2.0 * Wproj * Wproj));
        const downwashHeightFactor = smoothstep(obs.height * 0.2, obs.height * 1.0, y);
        const downwash = -downwashDecay * downwashHeightFactor *
          PREVIEW_MODEL_PARAMS.downwashStrength * baseSpeed * vertEnv;
        sumVy += downwash;
      }
    }

    // -------------------------------------------------------------------------
    // Bounded Multi-Obstacle Reconstruction (Sections K & L)
    // -------------------------------------------------------------------------

    // 1. Total wake deficit factor: (1 - totalWakeFactor) is aggregate deficit D_total
    const aggregateDeficit = 1.0 - totalWakeFactor;
    const wakeVelocityFactor = Math.max(0.15, 1.0 - aggregateDeficit);

    // 2. Aggregate flank acceleration: saturating hyperbolic tangent combination
    // Strictly bounded: ceiling at 1.0 + aggregateAccelMax (eliminates 1.25^N runaway)
    const aggregateAccel = 1.0 + PREVIEW_MODEL_PARAMS.aggregateAccelMax *
      Math.tanh(sumFlankAccel / PREVIEW_MODEL_PARAMS.aggregateAccelMax);

    // 3. Combined horizontal steering vector: magnitude clamp
    const maxSteerSpeed = baseSpeed * PREVIEW_MODEL_PARAMS.maxSteeringRatio;
    const steerMag = Math.hypot(steerVx, steerVz);
    if (steerMag > maxSteerSpeed && steerMag > 1e-4) {
      const steerClamp = maxSteerSpeed / steerMag;
      steerVx *= steerClamp;
      steerVz *= steerClamp;
    }

    // 4. Combined vertical deflection: safe magnitude clamp
    const maxVertical = baseSpeed * 0.50;
    const clampedSumVy = Math.max(-maxVertical, Math.min(maxVertical, sumVy));

    // Reconstruct final velocity vector
    let vx = vxAmb * wakeVelocityFactor * aggregateAccel + steerVx;
    let vy = vyAmb + clampedSumVy;
    let vz = vzAmb * wakeVelocityFactor * aggregateAccel + steerVz;

    // Numerical safety guard: guarantee finite velocity
    if (!Number.isFinite(vx) || !Number.isFinite(vy) || !Number.isFinite(vz)) {
      if (out) { out.vx = vxAmb; out.vy = vyAmb; out.vz = vzAmb; return out; }
      return { vx: vxAmb, vy: vyAmb, vz: vzAmb };
    }

    if (out) {
      out.vx = vx;
      out.vy = vy;
      out.vz = vz;
      return out;
    }

    return { vx, vy, vz };
  }

  /**
   * Applies physical solid penetration avoidance to keep a particle or probe point
   * outside solid building mass perimeters (Section M).
   * Uses geometry-aware oriented footprint distance and surface normal.
   */
  private applySolidPenetrationAvoidance(p: { x: number; y: number; z: number }): void {
    for (let o = 0; o < this.obstacles.length; o++) {
      const obs = this.obstacles[o];
      if (p.y >= obs.height) continue; // Flow cleanly passes over the roof

      const { clearance, nx: wallNx, nz: wallNz } = this.getObstacleClearanceAndNormal(obs, p.x, p.z);
      // Safety threshold: keep particle at least 0.25m outside the solid boundary
      const margin = 0.25;
      if (clearance < margin) {
        // Push outward along the geometry-aware outward surface normal without coordinate jumps
        const pushDist = (margin + 0.05) - clearance;
        p.x += wallNx * pushDist;
        p.z += wallNz * pushDist;
      }
    }
  }

  public update(dt: number): void {
    this.simTime += dt;

    // Primary ambient wind vector
    const Ux = Math.sin(this.windHeadingRad);
    const Uz = -Math.cos(this.windHeadingRad);

    const stepDt = Math.min(0.05, Math.max(0.005, dt));

    const Hx = this.domainHalfX;
    const Hz = this.domainHalfZ;
    const exitMargin = 4.0;

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];

      // 1. Evaluate velocity at particle position via centralized canonical field evaluator
      const { vx, vy, vz } = this.evaluatePreviewVelocityAt(
        p.x,
        p.y,
        p.z,
        this.simTime,
        { speedScale: p.speedScale, seed: p.seed }
      );

      // 2. Physical solid penetration avoidance
      this.applySolidPenetrationAvoidance(p);

      // Advect particle
      const curSpeed = Math.hypot(vx, vy, vz);
      p.speed = curSpeed;
      p.vx = vx;
      p.vy = vy;
      p.vz = vz;

      p.x += vx * stepDt * 2.2;
      p.y = Math.max(0.5, p.y + vy * stepDt * 2.2);
      p.z += vz * stepDt * 2.2;

      p.age++;

      // Downstream boundary exit detection across computational bounding box
      const exitedDownstreamX = Math.abs(Ux) > 0.05 && (Ux > 0 ? p.x > Hx + exitMargin : p.x < -Hx - exitMargin);
      const exitedDownstreamZ = Math.abs(Uz) > 0.05 && (Uz > 0 ? p.z > Hz + exitMargin : p.z < -Hz - exitMargin);
      const outOfBounds = Math.abs(p.x) > Hx + 25.0 || Math.abs(p.z) > Hz + 25.0 || p.y > this.domainHeight * 0.95 || p.y < 0.2;
      const expiredSafety = p.age > p.maxAge;

      if (exitedDownstreamX || exitedDownstreamZ || outOfBounds || expiredSafety) {
        this.resetParticle(p, true);
      }
    }

    // 2. Stage 03.4: Continuously advect visual tracer pool
    this.updateVisualTracers(stepDt);

    // 3. Stage 05.1: Update ground scalar preview field
    this.updateGroundScalarGrid(stepDt);
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  public render(
    viewProjMatrix: Float32Array,
    _cameraPos: [number, number, number]
  ): void {
    if (!this.gl) return;

    // 1. Render Ground Plane
    if (this.groundProgram && this.groundVao && this.groundVertexCount > 0) {
      this.gl.useProgram(this.groundProgram);
      if (this.uGroundViewProjLoc) {
        this.gl.uniformMatrix4fv(this.uGroundViewProjLoc, false, viewProjMatrix);
      }
      if (this.uGroundWindDirLoc) {
        this.gl.uniform2f(
          this.uGroundWindDirLoc,
          Math.sin(this.windHeadingRad),
          -Math.cos(this.windHeadingRad)
        );
      }
      if (this.uGroundDomainHalfLoc) {
        this.gl.uniform2f(this.uGroundDomainHalfLoc, this.domainHalfX, this.domainHalfZ);
      }

      if (this.groundTexture && this.uGroundTextureLoc) {
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.groundTexture);
        this.gl.uniform1i(this.uGroundTextureLoc, 0);
      }

      this.gl.bindVertexArray(this.groundVao);
      this.gl.drawArrays(this.gl.TRIANGLES, 0, this.groundVertexCount);
      this.gl.bindVertexArray(null);

      if (this.groundTexture) {
        this.gl.bindTexture(this.gl.TEXTURE_2D, null);
      }
    }

    // 2. Render Field-Sampled Streamlet Ribbons (Stage 03.2: spatial RK2 integration)
    if (this.flowProgram && this.flowVao && this.flowVbo && this.flowVertexBuffer) {
      const buf = this.flowVertexBuffer;
      let writeOffset = 0;

      const camX = Number.isFinite(_cameraPos?.[0]) ? _cameraPos[0] : 0.0;
      const camY = Number.isFinite(_cameraPos?.[1]) ? _cameraPos[1] : 60.0;
      const camZ = Number.isFinite(_cameraPos?.[2]) ? _cameraPos[2] : 120.0;

      const refSpeed = Math.max(0.5, this.windSpeed);
      const visualFieldTime = this.simTime;
      const step = VISUAL_PATH_STEP;
      const halfStep = step * 0.5;

      const pX = FlowPreviewRenderer.pathX;
      const pY = FlowPreviewRenderer.pathY;
      const pZ = FlowPreviewRenderer.pathZ;
      const pSpeed = FlowPreviewRenderer.pathSpeed;

      const nLx = FlowPreviewRenderer.nodeLx;
      const nLy = FlowPreviewRenderer.nodeLy;
      const nLz = FlowPreviewRenderer.nodeLz;
      const nRx = FlowPreviewRenderer.nodeRx;
      const nRy = FlowPreviewRenderer.nodeRy;
      const nRz = FlowPreviewRenderer.nodeRz;
      const nR = FlowPreviewRenderer.nodeR;
      const nG = FlowPreviewRenderer.nodeG;
      const nB = FlowPreviewRenderer.nodeB;
      const nA = FlowPreviewRenderer.nodeA;

      const vOut = FlowPreviewRenderer._velOut;
      const fParams = FlowPreviewRenderer._fieldParams;

      // Default ambient wind unit direction fallback
      const defUx = Math.sin(this.windHeadingRad);
      const defUz = -Math.cos(this.windHeadingRad);

      for (let i = 0; i < this.activeVisualTracerCount; i++) {
        const headX = this.tracerX[i];
        const headY = this.tracerY[i];
        const headZ = this.tracerZ[i];
        const tier = this.tracerTier[i];
        const baseAlpha = this.tracerBaseAlpha[i];

        fParams.speedScale = this.tracerSpeedScale[i];
        fParams.seed = this.tracerSeed[i];

        // Step 1: CURRENT TRACER HEAD (Node 0)
        this.evaluatePreviewVelocityAt(headX, headY, headZ, visualFieldTime, fParams, vOut);
        const s0 = Math.hypot(vOut.vx, vOut.vy, vOut.vz);
        pX[0] = headX;
        pY[0] = headY;
        pZ[0] = headZ;
        pSpeed[0] = s0;

        let u0x = defUx;
        let u0y = 0.0;
        let u0z = defUz;
        if (s0 > 0.05) {
          const invS0 = 1.0 / s0;
          u0x = vOut.vx * invS0;
          u0y = vOut.vy * invS0;
          u0z = vOut.vz * invS0;
        }

        let validSamples = 1;

        // Step 2: Backward spatial RK2 integration along upstream flow field
        for (let k = 0; k < VISUAL_PATH_SAMPLES - 1; k++) {
          const cx = pX[k];
          const cy = pY[k];
          const cz = pZ[k];

          let u1x = defUx;
          let u1y = 0.0;
          let u1z = defUz;
          if (k === 0) {
            u1x = u0x;
            u1y = u0y;
            u1z = u0z;
          } else {
            this.evaluatePreviewVelocityAt(cx, cy, cz, visualFieldTime, fParams, vOut);
            const sk = Math.hypot(vOut.vx, vOut.vy, vOut.vz);
            if (sk > 0.05) {
              const invSk = 1.0 / sk;
              u1x = vOut.vx * invSk;
              u1y = vOut.vy * invSk;
              u1z = vOut.vz * invSk;
            }
          }

          // Step backward (upstream) to midpoint
          const mx = cx - u1x * halfStep;
          const my = Math.max(0.2, cy - u1y * halfStep);
          const mz = cz - u1z * halfStep;

          // Sample k2 at midpoint
          this.evaluatePreviewVelocityAt(mx, my, mz, visualFieldTime, fParams, vOut);
          const sm = Math.hypot(vOut.vx, vOut.vy, vOut.vz);
          let u2x = u1x;
          let u2y = u1y;
          let u2z = u1z;
          if (sm > 0.05) {
            const invSm = 1.0 / sm;
            u2x = vOut.vx * invSm;
            u2y = vOut.vy * invSm;
            u2z = vOut.vz * invSm;
          }

          // Advance backward to next upstream path node
          const nx = cx - u2x * step;
          const ny = Math.max(0.2, cy - u2y * step);
          const nz = cz - u2z * step;

          // Solid obstacle avoidance: if upstream path intersects a solid building, terminate trail
          let hitSolid = false;
          for (let o = 0; o < this.obstacles.length; o++) {
            const obs = this.obstacles[o];
            if (ny >= obs.height) continue;
            if (Math.hypot(nx - obs.cx, nz - obs.cz) > obs.radius + 0.5) continue;
            const { clearance } = this.getObstacleClearanceAndNormal(obs, nx, nz);
            if (clearance <= 0.0) {
              hitSolid = true;
              break;
            }
          }
          if (hitSolid) break;

          pX[k + 1] = nx;
          pY[k + 1] = ny;
          pZ[k + 1] = nz;
          pSpeed[k + 1] = sm;
          validSamples = k + 2;
        }

        if (validSamples < 2) continue;

        // Step 3: Visual hierarchy & field interest modulation (stable over time, no phase pulse)
        let speedDevSum = 0.0;
        for (let k = 0; k < validSamples; k++) {
          speedDevSum += Math.abs(pSpeed[k] / refSpeed - 1.0);
        }
        const speedDev = speedDevSum / validSamples;

        // Trajectory curvature
        const seg0X = pX[0] - pX[1];
        const seg0Z = pZ[0] - pZ[1];
        const segEnd0X = pX[validSamples - 2] - pX[validSamples - 1];
        const segEnd0Z = pZ[validSamples - 2] - pZ[validSamples - 1];
        const len0 = Math.hypot(seg0X, seg0Z);
        const lenEnd = Math.hypot(segEnd0X, segEnd0Z);
        let pathTurn = 0.0;
        if (len0 > 0.01 && lenEnd > 0.01) {
          const dot = (seg0X * segEnd0X + seg0Z * segEnd0Z) / (len0 * lenEnd);
          pathTurn = Math.max(0.0, 1.0 - Math.max(-1.0, Math.min(1.0, dot)));
        }

        const tierBonus = tier === 2 ? 1.18 : (tier === 1 ? 1.08 : 1.0);
        const fieldInterest = Math.min(1.0, speedDev * 1.25 + pathTurn * 1.35);
        const streamletAlphaScale = baseAlpha * tierBonus * (0.88 + 0.32 * fieldInterest);

        // Step 4: Precompute ribbon strip node vertices
        let prevWx = 0.0;
        let prevWy = 0.0;
        let prevWz = 0.0;

        for (let k = 0; k < validSamples; k++) {
          const xk = pX[k];
          const yk = pY[k];
          const zk = pZ[k];

          // Tangent pointing forward in direction of FLOW (from upstream to downstream)
          let tx = 0.0;
          let ty = 0.0;
          let tz = 0.0;

          if (k === 0) {
            tx = pX[0] - pX[1];
            ty = pY[0] - pY[1];
            tz = pZ[0] - pZ[1];
          } else if (k === validSamples - 1) {
            tx = pX[k - 1] - pX[k];
            ty = pY[k - 1] - pY[k];
            tz = pZ[k - 1] - pZ[k];
          } else {
            tx = pX[k - 1] - pX[k + 1];
            ty = pY[k - 1] - pY[k + 1];
            tz = pZ[k - 1] - pZ[k + 1];
          }

          const tLen = Math.hypot(tx, ty, tz);
          if (tLen > 0.001) {
            const tInv = 1.0 / tLen;
            tx *= tInv; ty *= tInv; tz *= tInv;
          } else {
            tx = defUx; ty = 0.0; tz = defUz;
          }

          // View vector from trail node to camera
          let vdx = camX - xk;
          let vdy = camY - yk;
          let vdz = camZ - zk;
          const vdLen = Math.hypot(vdx, vdy, vdz);
          if (vdLen > 0.001) {
            const vdInv = 1.0 / vdLen;
            vdx *= vdInv; vdy *= vdInv; vdz *= vdInv;
          } else {
            vdx = 0.0; vdy = 1.0; vdz = 0.0;
          }

          // Width vector: cross(tangent, viewDir)
          let wx = ty * vdz - tz * vdy;
          let wy = tz * vdx - tx * vdz;
          let wz = tx * vdy - ty * vdx;
          const wLen = Math.hypot(wx, wy, wz);
          if (wLen > 0.001) {
            const wInv = 1.0 / wLen;
            wx *= wInv; wy *= wInv; wz *= wInv;
          } else {
            // Collinear fallback: world-up perpendicular
            wx = -tz; wy = 0.0; wz = tx;
            const wLen2 = Math.hypot(wx, wy, wz);
            if (wLen2 > 0.001) {
              const wInv2 = 1.0 / wLen2;
              wx *= wInv2; wy *= wInv2; wz *= wInv2;
            } else {
              wx = 1.0; wy = 0.0; wz = 0.0;
            }
          }

          // Stage 04: Anti-twisting sign continuity along strip to eliminate 180° ribbon flips
          if (k > 0) {
            const dotPrev = wx * prevWx + wy * prevWy + wz * prevWz;
            if (dotPrev < 0.0) {
              wx = -wx;
              wy = -wy;
              wz = -wz;
            }
          }
          prevWx = wx;
          prevWy = wy;
          prevWz = wz;

          const tParam = k / (validSamples - 1); // 0 at head, 1 at tail

          // Slender ribbon width: 0.10m half-width at head down to ~0.065m at tail (0.20m -> 0.13m full width)
          const hw = RIBBON_HALF_WIDTH * (1.0 - 0.35 * tParam);

          // Alpha fade: head is solid and crisp, tail tapers softly to 0.30
          const nodeTaper = 0.30 + 0.70 * Math.pow(1.0 - tParam, 1.15);
          const alpha = nodeTaper * streamletAlphaScale;

          // Local color from speed at this exact spatial path sample
          const sRatioNode = pSpeed[k] / refSpeed;
          const [cr, cg, cb] = this.velocityColor(sRatioNode);

          nLx[k] = xk - wx * hw;
          nLy[k] = yk - wy * hw;
          nLz[k] = zk - wz * hw;
          nRx[k] = xk + wx * hw;
          nRy[k] = yk + wy * hw;
          nRz[k] = zk + wz * hw;
          nR[k] = cr;
          nG[k] = cg;
          nB[k] = cb;
          nA[k] = alpha;
        }

        // Step 5: Emit continuous quads connecting adjacent nodes
        const numSegs = validSamples - 1;
        for (let s = 0; s < numSegs; s++) {
          const s1 = s + 1;
          const segDist = Math.hypot(
            pX[s] - pX[s1],
            pY[s] - pY[s1],
            pZ[s] - pZ[s1]
          );
          if (segDist < 0.005 || segDist > 25.0) continue;

          // Check buffer capacity (6 vertices × 8 floats = 48 floats per quad)
          if (writeOffset + 48 > buf.length) break;

          // Triangle 1: L0 (-1), R0 (+1), L1 (-1)
          buf[writeOffset++] = nLx[s];  buf[writeOffset++] = nLy[s];  buf[writeOffset++] = nLz[s];
          buf[writeOffset++] = nR[s];   buf[writeOffset++] = nG[s];   buf[writeOffset++] = nB[s];   buf[writeOffset++] = nA[s];
          buf[writeOffset++] = -1.0;

          buf[writeOffset++] = nRx[s];  buf[writeOffset++] = nRy[s];  buf[writeOffset++] = nRz[s];
          buf[writeOffset++] = nR[s];   buf[writeOffset++] = nG[s];   buf[writeOffset++] = nB[s];   buf[writeOffset++] = nA[s];
          buf[writeOffset++] = 1.0;

          buf[writeOffset++] = nLx[s1]; buf[writeOffset++] = nLy[s1]; buf[writeOffset++] = nLz[s1];
          buf[writeOffset++] = nR[s1];  buf[writeOffset++] = nG[s1];  buf[writeOffset++] = nB[s1];  buf[writeOffset++] = nA[s1];
          buf[writeOffset++] = -1.0;

          // Triangle 2: R0 (+1), R1 (+1), L1 (-1)
          buf[writeOffset++] = nRx[s];  buf[writeOffset++] = nRy[s];  buf[writeOffset++] = nRz[s];
          buf[writeOffset++] = nR[s];   buf[writeOffset++] = nG[s];   buf[writeOffset++] = nB[s];   buf[writeOffset++] = nA[s];
          buf[writeOffset++] = 1.0;

          buf[writeOffset++] = nRx[s1]; buf[writeOffset++] = nRy[s1]; buf[writeOffset++] = nRz[s1];
          buf[writeOffset++] = nR[s1];  buf[writeOffset++] = nG[s1];  buf[writeOffset++] = nB[s1];  buf[writeOffset++] = nA[s1];
          buf[writeOffset++] = 1.0;

          buf[writeOffset++] = nLx[s1]; buf[writeOffset++] = nLy[s1]; buf[writeOffset++] = nLz[s1];
          buf[writeOffset++] = nR[s1];  buf[writeOffset++] = nG[s1];  buf[writeOffset++] = nB[s1];  buf[writeOffset++] = nA[s1];
          buf[writeOffset++] = -1.0;
        }
      }

      const triVertexCount = writeOffset / 8;

      if (triVertexCount > 0) {
        this.gl.useProgram(this.flowProgram);
        if (this.uFlowViewProjLoc) {
          this.gl.uniformMatrix4fv(this.uFlowViewProjLoc, false, viewProjMatrix);
        }

        // Enable alpha blending for translucent ribbons (premultiplied)
        this.gl.enable(this.gl.BLEND);
        this.gl.blendFunc(this.gl.ONE, this.gl.ONE_MINUS_SRC_ALPHA);

        // Depth test ON but depth write OFF for translucent ribbons
        this.gl.enable(this.gl.DEPTH_TEST);
        this.gl.depthMask(false);

        this.gl.bindVertexArray(this.flowVao);
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.flowVbo);
        this.gl.bufferData(
          this.gl.ARRAY_BUFFER,
          buf.subarray(0, writeOffset),
          this.gl.DYNAMIC_DRAW
        );

        this.gl.drawArrays(this.gl.TRIANGLES, 0, triVertexCount);
        this.gl.bindVertexArray(null);

        // Restore depth write
        this.gl.depthMask(true);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Maps normalized velocity magnitude (sRatio = speed / ambientWindSpeed) to the authoritative
   * airflow palette (deep blue -> blue -> strong yellow -> yellow-orange -> orange -> red).
   */
  private velocityColor(sRatio: number): [number, number, number] {
    return evaluateAirflowColor(sRatio);
  }

  private compileShader(type: number, source: string): WebGLShader | null {
    if (!this.gl) return null;
    const shader = this.gl.createShader(type);
    if (!shader) return null;

    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);

    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      console.error('[FlowPreviewRenderer] Shader compile error:', this.gl.getShaderInfoLog(shader));
      this.gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  public destroy(): void {
    if (!this.gl) return;

    if (this.groundVbo) this.gl.deleteBuffer(this.groundVbo);
    if (this.groundVao) this.gl.deleteVertexArray(this.groundVao);
    if (this.groundProgram) this.gl.deleteProgram(this.groundProgram);
    if (this.groundTexture) this.gl.deleteTexture(this.groundTexture);

    if (this.flowVbo) this.gl.deleteBuffer(this.flowVbo);
    if (this.flowVao) this.gl.deleteVertexArray(this.flowVao);
    if (this.flowProgram) this.gl.deleteProgram(this.flowProgram);

    this.flowVertexBuffer = null;
    this.particles = [];
    this.obstacles = [];
    this.activeVisualTracerCount = 0;
    this.tracerCount = 0;
    this.gl = null;
  }
}
